// Third-party
import React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Waypoint } from 'react-waypoint';

// Components
import ActivityCard from './ActivityCard';
import ActivityListItem from './ActivityListItem';
import { LoadingSpinner } from '../common';
import { GenericEmptyState } from '../misc';

// Utils
import { ACTIVITY_FORMAT } from '../../../utils/activity-log';
import { formatMessage } from '../../../utils/i18n';
import Activity from '@/types/activity';

type Props = {
  activityLogs?: Activity[];
  loading: boolean;
  format: ACTIVITY_FORMAT;
  inline?: boolean;
  displayAsCards?: boolean;
  hasMore: boolean;
  onLoadMoreActivity: () => void;
  emptyStateText?: string;
  loadingStateText?: string;
  viewContactActivity: (id: string) => void;
  scrollAncestor?: Window | HTMLElement | string;
};

type State = {
  initialized: boolean;
};

class ActivityList extends React.Component<Props, State> {
  private scrollContainer?: HTMLElement;

  static defaultProps = {
    viewContactActivity: () => {},
  };

  readonly state: State = {
    initialized: false,
  };

  setContainerRef = (element: HTMLElement) => {
    this.scrollContainer = element;

    if (!this.state.initialized) {
      this.setState({ initialized: true });
    }
  };

  renderEmptyState = () => {
    const { emptyStateText } = this.props;

    return <GenericEmptyState header={emptyStateText || formatMessage('EmptyState.EmptyData')} />;
  };

  renderActivityLog = () => {
    const { activityLogs, format, inline, displayAsCards } = this.props;

    if (!activityLogs || !activityLogs.length) {
      return null;
    }

    return activityLogs.map((activity: Activity) => {
      if (displayAsCards) {
        return <ActivityCard key={activity._id} activity={activity} format={format} viewContactActivity={this.props.viewContactActivity} />;
      }

      return <ActivityListItem key={activity._id} activity={activity} format={format} inline={inline} />;
    });
  };

  getScrollableAncestor = () => {
    const { scrollAncestor } = this.props;

    if (scrollAncestor) {
      if (typeof scrollAncestor === 'string') {
        return document.querySelector(scrollAncestor);
      }

      return scrollAncestor;
    }

    return this.scrollContainer;
  };

  render() {
    const { activityLogs, loading, hasMore, loadingStateText } = this.props;
    const { initialized } = this.state;

    const showEmptyState = activityLogs && activityLogs.length === 0 && !loading;
    const showLoadingState = (!activityLogs || !activityLogs.length) && loading;
    const showActivityLog = activityLogs && activityLogs.length > 0;

    return (
      <PerfectScrollbar containerRef={this.setContainerRef} className="activity-list">
        {showEmptyState && this.renderEmptyState()}

        {showLoadingState && <LoadingSpinner className="initial-loading" message={loadingStateText} />}

        {showActivityLog && this.renderActivityLog()}

        {initialized && !loading && activityLogs && hasMore && (
          <>
            <LoadingSpinner />
            <Waypoint scrollableAncestor={this.getScrollableAncestor()} onEnter={this.props.onLoadMoreActivity} />
          </>
        )}
      </PerfectScrollbar>
    );
  }
}

export default ActivityList;
